import * as React from 'react';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  List,
  ListItem,
  TextField
} from '@material-ui/core';
import { MonthlyReport } from 'redux/ducks/monthlyReports';

type DataModalProps = {
  open: boolean;
  data: MonthlyReport['data'];
  onClose: () => void;
};

type RootRegistrationItem = {
  name: string;
  amount: number;
};

const isRootRegistrationsField = (fieldKey: string): boolean => fieldKey === 'topWaste';

const TopWasteList: React.FC<{ value: RootRegistrationItem[] }> = ({ value }) => {
  return (
    <List dense disablePadding>
      {value.map((item, index) => (
        <ListItem key={`${index}-${item.name}-${item.amount}`} disableGutters>
          <TextField
            label='Name'
            value={item.name}
            variant='outlined'
            size='small'
            disabled
            style={{ marginRight: 8, flex: 1 }}
          />
          <TextField
            label='Amount'
            type='number'
            value={item.amount}
            variant='outlined'
            size='small'
            disabled
            style={{ width: 120 }}
          />
        </ListItem>
      ))}
    </List>
  );
};

// deliberately stringifies whatever a data field holds; the rule cannot express
// that any input is acceptable here
// eslint-disable-next-line @typescript-eslint/no-base-to-string
const getInputValue = (value: unknown): string => String(value);

type DataFieldRendererProps = {
  fieldKey: string;
  value: unknown;
};

const DataFieldRenderer: React.FC<DataFieldRendererProps> = ({ fieldKey, value }) => {
  if (isRootRegistrationsField(fieldKey) && Array.isArray(value)) {
    return <TopWasteList value={value} />;
  }

  return <TextField value={getInputValue(value)} variant='outlined' fullWidth disabled multiline />;
};

const DataModal: React.FC<DataModalProps> = ({ open, data, onClose }) => {
  const intl = useIntl();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <span>{intl.formatMessage({ id: 'admin.monthlyReports.input' })}</span>
      </DialogTitle>
      <DialogContent>
        {Object.keys(data).map((key) => (
          <FormControl key={key} fullWidth style={{ marginBottom: 12 }}>
            <FormLabel style={{ marginBottom: 6 }}>{key}</FormLabel>
            <DataFieldRenderer fieldKey={key} value={data[key]} />
          </FormControl>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {intl.formatMessage({ id: 'base.ok', defaultMessage: 'Ok' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DataModal;
